import {
  Backdrop,
  Button,
  Grid,
  Modal,
  Paper,
  Tab,
  Tabs,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { unMask } from "remask";
import useApi from "../../../../../../../hooks/api";
import {
  CurrencyField,
  DateField,
  DateTimeField,
  DocumentField,
  MultiSelectField,
  RadioField,
  TextField,
} from "./fields";

const tradePhaseFieldsMapping = {
  value: {
    FieldComponent: CurrencyField,
  },
  date: {
    FieldComponent: DateField,
  },
  datetime: {
    FieldComponent: DateTimeField,
  },
  document: {
    FieldComponent: DocumentField,
  },
  checklist: {
    FieldComponent: MultiSelectField,
  },
  radio: {
    FieldComponent: RadioField,
  },
  text: {
    FieldComponent: TextField,
    fieldProps: {
      rows: 3,
    },
  },
};

const TradePhaseField = (props) => {
  const {
    field: { data_type },
  } = props;

  const { FieldComponent, fieldProps } = tradePhaseFieldsMapping[data_type] || {
    FieldComponent: TextField,
  };

  return <FieldComponent {...fieldProps} {...props} />;
};

const FinishTradeButton = ({ trade, modalProps, ...restProps }) => {
  const nameCheck = trade.current_phase.name === "Oportunidade";
  const isDoneCheck = trade.is_done;

  if (nameCheck || isDoneCheck) return null;

  const { onSuccessClick, onFailedClick, ...restModalProps } = modalProps;

  const buttons = [
    {
      label: "Precatório comprado",
      onClick: onSuccessClick,
      id: "negotitation-success-btn",
    },
    {
      label: "Negociação perdida",
      onClick: onFailedClick,
      id: "negotitation-fail-btn",
    },
  ];

  return (
    <>
      <Button
        disabled={trade.current_phase.done}
        className="card-modal-field-edit trade-finish-btn"
        variant="outlined"
        fullWidth
        {...restProps}
      >
        Finalizar negociação
      </Button>

      <Modal
        aria-describedby="modal para finalizar negociação"
        aria-labelledby="finalizar-negociação"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        {...restModalProps}
      >
        <div className="crm-trade-finish-modal">
          <Paper elevation={3}>
            <div className="p-30">
              {buttons.map(({ label, ...buttonProps }) => (
                <Button
                  key={buttonProps.id}
                  disabled={trade.current_phase.done}
                  className="crm-trade-field-edit"
                  variant="outlined"
                  {...buttonProps}
                >
                  {label}
                </Button>
              ))}
            </div>
          </Paper>
        </div>
      </Modal>
    </>
  );
};

const NextPhaseButton = ({ trade, ...restProps }) => {
  const phaseName = trade.current_phase.name;

  if (phaseName === "Perdido" || phaseName === "Comprado") return null;

  return (
    <Button
      className="crm-trade-field-edit next-phase-btn"
      disabled={phaseName === "Auditoria" && !trade.is_done}
      variant="outlined"
      {...restProps}
    >
      Ir para próxima fase
    </Button>
  );
};

export default ({ context }) => {
  const { patch } = useApi();
  const trade = context.trade;

  const [currentFields, setCurrentFields] = useState([]);
  const [dialogOpen, toggleDialog] = useState(false);

  const setNewPhase = async (action) => {
    try {
      const { data } = await patch(`/v1/crm/trades/${trade.id}/update_phase`, {
        action,
      });

      context.setTrade(data);
    } catch (e) {
      context.setError(e);
    }
  };

  const updateField = async ({ target }) => {
    const { name: targetName } = target;
    let value = target.value;

    const field = currentFields.find(({ id, name }) =>
      targetName
        ? name === targetName
        : id == parseInt(target.getAttribute("fieldid"))
    );

    if (field.name == "cpf_cnpj") {
      value = unMask(value);
    }

    if (field.data_type == "value") {
      value = parseInt(value.replace(/\D/g, ""));
    }

    if (value && field.value != value) {
      let index = currentFields.findIndex((e) => e.field_id == field.field_id);
      let arr = [...currentFields];

      arr[index] = { ...arr[index], updating: true };

      setCurrentFields(arr);

      try {
        await patch(`/v1/crm/trades/${trade.id}/fields/${field.field_id}`, {
          value,
        });

        let arr = [...currentFields];
        arr[index] = { ...arr[index], updating: false, value: value };

        setCurrentFields(arr);
      } catch {
        let arr = [...currentFields];

        arr[index] = {
          ...arr[index],
          updating: false,
          icon: "fas fa-exclamation-circle m-color-red_opacity_0_4",
        };

        setCurrentFields(arr);
      }
    }
  };

  useEffect(() => {
    const cf = trade.fields.filter((e) => {
      if (e.crm_phase_id == trade.current_phase.id) return e;
    });

    setCurrentFields(cf);
    toggleDialog(false);
  }, [trade.current_phase]);

  return (
    <>
      <Tabs
        indicatorColor="primary"
        textColor="primary"
        value={trade.current_phase.id}
        aria-label="trade phases"
        variant="scrollable"
        scrollButtons="auto"
      >
        {trade.phases.map(({ id, name }) => (
          <Tab key={id} label={name} value={id} disableRipple={true} />
        ))}
      </Tabs>

      <Grid container>
        <div className="p-30 w-100">
          <div id="crm-trade-data-block">
            {currentFields.map((field) => {
              const { name, data_type } = field;

              return (
                name !== "precatorio_id" &&
                data_type !== "attachments" && (
                  <TradePhaseField
                    key={name}
                    field={field}
                    className="crm-trade-field-edit"
                    handleBlur={updateField}
                  />
                )
              );
            })}
          </div>

          <div id="crm-buttons-block" className="mt-20">
            <Button
              className="crm-trade-field-edit next-phase-btn"
              disabled={trade.current_phase.name == "Oportunidade"}
              variant="outlined"
              onClick={() => setNewPhase("previous")}
            >
              Voltar para fase anterior
            </Button>

            <NextPhaseButton
              trade={trade}
              onClick={() => setNewPhase("next")}
            />

            <FinishTradeButton
              trade={trade}
              onClick={() => toggleDialog(true)}
              modalProps={{
                open: dialogOpen,
                onClose: () => toggleDialog(false),
                onSuccessClick: () => setNewPhase("success"),
                onFailedClick: () => setNewPhase("failed"),
              }}
            />
          </div>
        </div>
      </Grid>
    </>
  );
};
